import { useEffect, useRef } from "react";

const VERT_SRC = `
attribute vec2 a_position;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const FRAG_SRC = `
precision highp float;
uniform float u_time;
uniform vec2  u_resolution;

vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i  = floor(v + dot(v, C.yy) );
  vec2 x0 = v -   i + dot(i, C.xx);
  vec2 i1; i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod289(i);
  vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 )) + i.x + vec3(0.0, i1.x, 1.0 ));
  vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
  m = m*m ; m = m*m ;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );
  vec3 g;
  g.x  = a0.x  * x0.x  + h.x  * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

void main() {
  vec2 p = (gl_FragCoord.xy * 2.0 - u_resolution.xy) / min(u_resolution.x, u_resolution.y);
  float r = length(p);
  float a = atan(p.y, p.x);
  float t = u_time * 0.7; // Vel da espiral
  
  // Distorção da Coordenada transformando em um Vórtice Torcido
  float twirl = a + 4.0 * pow(r, 0.6) - t * 2.5;
  
  // Transformando de volta para coordenadas cartesianas distorcidas
  vec2 target = vec2(cos(twirl), sin(twirl)) * r;
  
  // Sistema fractal de Ruído Fbm Simplex
  float f1 = snoise(target * 2.5 - t * 0.4);
  float f2 = snoise(target * 5.0 + t * 0.6);
  float f3 = snoise(target * 10.0 - t * 1.2);
  
  float val = f1 * 0.5 + f2 * 0.25 + f3 * 0.125;
  
  // O vórtice no meio expele a luz, as bordas ficam escuras
  val += (1.0 - r)*0.8 - 0.2;
  
  // Paleta de cores retiradas da imagem original do Portal
  vec3 cDarkest = vec3(0.06, 0.29, 0.08); // #114a16
  vec3 cBase    = vec3(0.18, 0.70, 0.14); // #2eb424
  vec3 cMid     = vec3(0.42, 0.89, 0.18); // #6be42f
  vec3 cLime    = vec3(0.69, 0.96, 0.20); // #b2f735
  vec3 cWhite   = vec3(0.90, 0.90, 0.90); // #dbdbdb
  
  // Camadas duras (Hard step boundaries) como VETOR ART
  vec3 col = cDarkest;
  if(val > -0.15) col = cBase;
  if(val > 0.15)  col = cMid;
  if(val > 0.45)  col = cLime;
  
  // Pequenas particulas e "gaps" do plasma voando
  float specks1 = snoise(p*7.0 + t);
  float specks2 = snoise(p*14.0 - t*1.5);
  float sVal = specks1 * 0.5 + specks2 * 0.5;
  
  // Adiciona flutuantes isolados
  if(sVal > 0.5 && r > 0.2) {
      col = cLime;
      if(sVal > 0.7) {
         col = cWhite; // pontinhos mais isolados da cena
      }
  }

  // Desvanecimento bem sombrio nas bordas extremas da tela igual a foto
  float vignette = smoothstep(1.5, 0.5, r);
  col = mix(cDarkest * 0.5, col, vignette);

  gl_FragColor = vec4(col, 1.0);
}
`;

function createShader(gl: WebGLRenderingContext, type: number, src: string) {
  const s = gl.createShader(type);
  if (!s) return null;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.error(gl.getShaderInfoLog(s));
  }
  return s;
}

function createProgram(gl: WebGLRenderingContext, vert: string, frag: string) {
  const vs = createShader(gl, gl.VERTEX_SHADER, vert);
  const fs = createShader(gl, gl.FRAGMENT_SHADER, frag);
  if (!vs || !fs) return null;
  const prog = gl.createProgram();
  if (!prog) return null;
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  return prog;
}

export default function WebGLPortalBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef    = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext("webgl", { alpha: false, antialias: false });
    if (!gl) return;
    const program = createProgram(gl, VERT_SRC, FRAG_SRC);
    if (!program) return;
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
    const posLoc  = gl.getAttribLocation(program, "a_position");
    const timeLoc = gl.getUniformLocation(program, "u_time");
    const resLoc  = gl.getUniformLocation(program, "u_resolution");
    if (gl.isContextLost()) return;
    gl.useProgram(program);
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    let start: number | null = null;
    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();

    const render = (ts: number) => {
      if (start === null) start = ts;
      const t = (ts - start) / 1000;
      if (gl.isContextLost()) return;
      gl.uniform1f(timeLoc, t);
      gl.uniform2f(resLoc, canvas.width, canvas.height);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(rafRef.current);
      observer.disconnect();
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />;
}
